(function () {
  if (customElements.get('cogi-games-search')) return;

  function norm(s) {
    return String(s).toLowerCase().normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9一-鿿]+/g, ' ')
      .trim();
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  class CogiGamesSearch extends HTMLElement {
    connectedCallback() {
      this._cogi_input = this.querySelector('.cgs__input');
      this._cogi_out = this.querySelector('.cgs__results');
      if (!this._cogi_input || !this._cogi_out) return;

      // Bitmask of the card(s) actually shipped: 1 = small card, 2 = large card.
      // Titles that live only on a card we no longer sell must not return a hit.
      this._cogi_mask = parseInt(this.dataset.cardMask, 10) || 2;
      this._cogi_hitNote = this.dataset.hitNote || '';
      this._cogi_email = this.dataset.email || '';
      this._cogi_max = parseInt(this.dataset.max, 10) || 20;
      this._cogi_ctaLabel = this.dataset.ctaLabel || '';
      this._cogi_ctaHref = this.dataset.ctaHref || '';
      this._cogi_data = null;
      this._cogi_loading = false;

      var self = this;

      this._cogi_input.addEventListener('focus', function () { self.load(); }, { once: true });
      this._cogi_input.addEventListener('input', function () {
        clearTimeout(self._cogi_timer);
        self._cogi_timer = setTimeout(function () { self.search(); }, 180);
      });

      // Chips make the first interaction a tap rather than a typing task.
      this.querySelectorAll('.cgs__chip').forEach(function (chip) {
        chip.addEventListener('click', function () {
          self._cogi_input.value = chip.dataset.query || chip.textContent.trim();
          self.stopPlaceholderCycle();
          self.load();
          self.search();
          self._cogi_input.focus();
        });
      });

      this.startPlaceholderCycle();
    }

    disconnectedCallback() {
      this.stopPlaceholderCycle();
      clearTimeout(this._cogi_timer);
    }

    /* ---------------------------------------------------------------- placeholder */

    startPlaceholderCycle() {
      var raw = this.dataset.placeholderCycle || '';
      var list = raw.split('|').map(function (s) { return s.trim(); }).filter(Boolean);
      if (list.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var self = this;
      var i = 0;
      this._cogi_cycle = setInterval(function () {
        if (document.activeElement === self._cogi_input || self._cogi_input.value) return;
        i = (i + 1) % list.length;
        self._cogi_input.setAttribute('placeholder', list[i]);
      }, 2400);
    }

    stopPlaceholderCycle() {
      if (this._cogi_cycle) { clearInterval(this._cogi_cycle); this._cogi_cycle = null; }
    }

    /* ---------------------------------------------------------------- data */

    load() {
      if (this._cogi_data || this._cogi_loading) return;
      this._cogi_loading = true;
      var self = this;
      fetch(this.dataset.url)
        .then(function (r) { return r.json(); })
        .then(function (rows) {
          var mask = self._cogi_mask;
          self._cogi_data = rows
            .filter(function (r) { return (r[1] & mask) !== 0; })
            .map(function (r) { return { n: r[0], s: norm(r[0]) }; });
        })
        .catch(function () {
          self._cogi_data = [];
          self._cogi_out.innerHTML = '<div class="cgs__panel cgs__panel--miss"><p class="cgs__miss-title">Could not load the game list.</p><p class="cgs__miss-note">Please refresh the page and try again.</p></div>';
        })
        .finally(function () {
          self._cogi_loading = false;
          if (self._cogi_input.value.trim()) self.search();
        });
    }

    /* ---------------------------------------------------------------- search */

    search() {
      var raw = this._cogi_input.value.trim();
      var q = norm(raw);

      if (!q) { this._cogi_out.innerHTML = ''; return; }

      if (!this._cogi_data) {
        this._cogi_out.innerHTML = '<div class="cgs__panel"><p class="cgs__miss-note">Loading the game list…</p></div>';
        this.load();
        return;
      }

      var words = q.split(' ');
      var hits = this._cogi_data.filter(function (g) {
        for (var i = 0; i < words.length; i++) {
          if (g.s.indexOf(words[i]) === -1) return false;
        }
        return true;
      }).sort(function (a, b) {
        var ap = a.s.indexOf(q) === 0 ? 0 : 1;
        var bp = b.s.indexOf(q) === 0 ? 0 : 1;
        if (ap !== bp) return ap - bp;
        if (a.s.length !== b.s.length) return a.s.length - b.s.length;
        return a.n.localeCompare(b.n);
      });

      if (hits.length === 0) { this.renderMiss(raw); return; }
      this.renderHit(hits, raw);
    }

    renderMiss(raw) {
      var mail = this._cogi_email
        ? ' or email <a href="mailto:' + esc(this._cogi_email) + '">' + esc(this._cogi_email) + '</a> and we will check the card for you before you order.'
        : '.';
      this._cogi_out.innerHTML =
        '<div class="cgs__panel cgs__panel--miss">' +
          '<p class="cgs__miss-title">Not on the card under that name.</p>' +
          '<p class="cgs__miss-note">Some classics are listed by their original or US title — try that' + mail + '</p>' +
        '</div>';
    }

    renderHit(hits, raw) {
      var top = hits[0];
      var rest = hits.slice(1, this._cogi_max);

      var cta = '';
      if (this._cogi_ctaLabel && this._cogi_ctaHref) {
        cta = '<a class="cgs__cta" href="' + esc(this._cogi_ctaHref) + '">' + esc(this._cogi_ctaLabel) + '</a>';
      }

      var html =
        '<div class="cgs__panel">' +
          '<div class="cgs__hit">' +
            '<span class="cgs__check" aria-hidden="true">' +
              '<svg viewBox="0 0 16 16" width="12" height="12"><path d="M6.2 11.1 3.3 8.2l1.1-1.1 1.8 1.8 4.4-4.4 1.1 1.1z" fill="currentColor"/></svg>' +
            '</span>' +
            '<span class="cgs__hit-text">' +
              '<span class="cgs__hit-name">' + esc(top.n) + '</span>' +
              (this._cogi_hitNote ? '<span class="cgs__hit-sub">' + esc(this._cogi_hitNote) + '</span>' : '') +
            '</span>' +
            cta +
          '</div>';

      if (rest.length) {
        html += '<ul class="cgs__more">' + rest.map(function (g) {
          return '<li class="cgs__more-item">' + esc(g.n) + '</li>';
        }).join('') + '</ul>';
      }

      if (hits.length > 1 + rest.length) {
        html += '<p class="cgs__miss-note">+ ' + (hits.length - 1 - rest.length) +
                ' more matching &ldquo;' + esc(raw) + '&rdquo;</p>';
      }

      html += '</div>';
      this._cogi_out.innerHTML = html;
    }
  }

  customElements.define('cogi-games-search', CogiGamesSearch);
})();
